import React, { useState, useRef, useEffect } from "react";
import i18n from "../../../i18n";
import { useGetLanguagesQuery } from "../../../services/api/language.api";
import styles from "./languageToSummary.module.css";
import { tWithKeyAttr as t } from "../../../i18nHelper";

type LanguageSelectorProps = {
  onSelectLanguage: (lang: string) => void;
};

const LanguageToSummary: React.FC<LanguageSelectorProps> = ({
  onSelectLanguage,
}) => {
  const { data: languages = [] } = useGetLanguagesQuery();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(i18n.language || "");
  const [tempSelected, setTempSelected] = useState(selected);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLang = languages.find((l) => l.code === selected);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function onSelectTemp(code: string) {
    setTempSelected(code);
  }

  function onConfirm() {
    setSelected(tempSelected);
    onSelectLanguage(tempSelected);
    setOpen(false);
  }

  return (
    <div className={styles.languageToSummaryContainer} ref={containerRef}>
      <div className={styles.languageDescription}>
        {t("CHOOSE_LANGUAGE_FOR_REPORT")}
      </div>

      <div
        className={styles.languageSelector}
        onClick={() => setOpen((prev) => !prev)}
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {currentLang && (
          <img
            src={`https://flagcdn.com/w40/${currentLang.countryCode.toLowerCase()}.png`}
            alt={currentLang.name}
            className={styles.flagImage}
          />
        )}
        <span className={styles.selectedText}>
          {currentLang ? currentLang.name : t("SELECT_YOUR_LANGUAGE")}
        </span>
        <span className={styles.arrow}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <>
          <ul className={styles.dropdownList} role="listbox">
            {languages.map((lang) => (
              <li
                key={lang.code}
                className={`${styles.dropdownItem} ${
                  lang.code === tempSelected ? styles.selectedTemp : ""
                }`}
                role="option"
                aria-selected={lang.code === tempSelected}
                onClick={() => onSelectTemp(lang.code)}
                onKeyDown={(e) => e.key === "Enter" && onSelectTemp(lang.code)}
                tabIndex={0}
              >
                <img
                  src={`https://flagcdn.com/w40/${lang.countryCode.toLowerCase()}.png`}
                  alt={lang.name}
                  className={styles.flagImage}
                />
                <span>{lang.name}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <button onClick={onConfirm} className={styles.confirmButton}>
        {t("CONFIRM_LANGUAGE")}
      </button>
    </div>
  );
};

export default LanguageToSummary;
